import { Button } from 'antd';
import React, { useState } from 'react';
import styled from 'styled-components';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';

import { FormWrapper } from '../../../WidgetConfiguratorComponents/FormGroups';
import ThresholdDatabilityRelationLine from './Threshold/ThresholdDatabilityRelationLine';
import ThresholdEditor from './Threshold/ThresholdEditor';

const FullWidthGrid = styled.div`
    grid-column: 1/6;
`;

const Line = styled.div`
    ${({ inActive }) => inActive && 'opacity: 0.2'}
`;

const DatabilityLine = ({ onAdd, label, activeRef, comapreRefs }) => {
    const ref = React.useRef();

    const inActive = comapreRefs && activeRef !== ref;

    return (
        <FullWidthGrid ref={ref}>
            <Line inActive={inActive}>
                <Button size='small' icon={<PlusOutlined />} shape='circle' onClick={() => onAdd(ref)} style={{ marginRight: 10 }} />
                <h3 style={{ display: 'inline' }}>
                    <strong>{label}</strong>
                </h3>
            </Line>
        </FullWidthGrid>
    );
};

export const sortThresholds = (a, b) => {
    if (a.value === b.value) {
        const order = ['equal', 'notEqual', 'lessThan', 'lessThanOrEqual', 'greaterThan', 'greaterThanOrEqual'];
        return order.indexOf(a.operator) - order.indexOf(b.operator);
    }
    return a.value > b.value ? 1 : -1;
};

const ThresholdConfigurator = ({ viewblockState, setViewblockState }) => {
    const [thresholdToEdit, setThresholdToEdit] = useState(undefined);
    const [displayThresholdForm, setDisplayThresholdForm] = useState(false);
    const [databilityThresholdRef, setDatabilityThresholdRef] = useState(undefined);

    const clearAndHideThresholdForm = () => {
        setThresholdToEdit(undefined);
        setDisplayThresholdForm(false);
        setDatabilityThresholdRef(undefined);
    };

    const onThresholdEdit = (threshold) => {
        setThresholdToEdit(threshold);
        setDisplayThresholdForm(true);
        setDatabilityThresholdRef(undefined);
    };

    const onThresholdAdd = (itemId, thresholdRef) => {
        setThresholdToEdit({
            itemId,
        });
        setDisplayThresholdForm(true);
        setDatabilityThresholdRef(thresholdRef);
    };

    const findThresholdsForHeading = (headingId, databilityIndex) =>
        viewblockState.settings.limits.filter(({ itemId }) => itemId === `${headingId}-${databilityIndex}`);

    const findAppearanceByThreshold = (threshold) => viewblockState.settings.threatLimits.find(({ id }) => id === threshold.threatLimitId);

    const onThresholdDelete = (threshold) => {
        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                limits: prev.settings.limits.filter((limit) => limit !== threshold),
            },
        }));

        if (threshold === thresholdToEdit) {
            clearAndHideThresholdForm();
        }
    };

    const onEditorSave = (threshold) => {
        setViewblockState((prev) => {
            const isEditForm = prev.settings.limits.find((limit) => limit === thresholdToEdit);

            return isEditForm
                ? {
                      ...prev,
                      settings: {
                          ...prev.settings,
                          limits: prev.settings.limits.map((limit) => (limit === thresholdToEdit ? threshold : limit)),
                      },
                  }
                : {
                      ...prev,
                      settings: {
                          ...prev.settings,
                          limits: [...prev.settings.limits, threshold],
                      },
                  };
        });

        clearAndHideThresholdForm();
    };

    return (
        <FormWrapper>
            {viewblockState.settings.headings.map((heading) =>
                heading.databilityLabels.map((label, databilityIndex) => (
                    <React.Fragment key={databilityIndex}>
                        <DatabilityLine
                            onAdd={(ref) => onThresholdAdd(`${heading.id}-${databilityIndex}`, ref)}
                            label={label}
                            comapreRefs={thresholdToEdit || databilityThresholdRef}
                            activeRef={databilityThresholdRef}
                        />
                        <FullWidthGrid>
                            {findThresholdsForHeading(heading.id, databilityIndex)
                                .sort(sortThresholds)
                                .map((threshold, index) => {
                                    const appearance = findAppearanceByThreshold(threshold);
                                    return (
                                        <ThresholdDatabilityRelationLine
                                            key={`${threshold.itemId}-${index}`}
                                            inActive={thresholdToEdit || databilityThresholdRef ? threshold !== thresholdToEdit : false}
                                            appearance={appearance}
                                            threshold={threshold}
                                            onDelete={() => onThresholdDelete(threshold)}
                                            onEdit={() => onThresholdEdit(threshold)}
                                        />
                                    );
                                })}
                        </FullWidthGrid>
                    </React.Fragment>
                ))
            )}
            {displayThresholdForm && (
                <FullWidthGrid>
                    <ThresholdEditor
                        onSave={onEditorSave}
                        onCancel={clearAndHideThresholdForm}
                        threshold={thresholdToEdit}
                        threatLimits={viewblockState.settings.threatLimits}
                    />
                </FullWidthGrid>
            )}
        </FormWrapper>
    );
};

export default ThresholdConfigurator;
